/* Lastoya theme — Ajax cart: drawer, add/change/remove, free-shipping progress */
(() => {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const currency = document.body.dataset.currency || 'USD';
  const freeShippingCents = Math.round(parseFloat(document.body.dataset.freeShipping || '0') * 100);

  const money = (cents) =>
    new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);

  /* ── Scroll lock (shared with theme.js) ── */
  window.__scrollLocks = window.__scrollLocks || 0;
  const lockScroll = () => {
    window.__scrollLocks += 1;
    document.documentElement.style.overflow = 'hidden';
  };
  const unlockScroll = () => {
    window.__scrollLocks = Math.max(0, window.__scrollLocks - 1);
    if (window.__scrollLocks === 0) document.documentElement.style.overflow = '';
  };

  /* ── Icons (match snippets/icon.liquid) ── */
  const svg = (size, stroke, paths) =>
    `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="${stroke}" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths}</svg>`;
  const ICONS = {
    cart: (size, stroke) =>
      svg(size, stroke, '<path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/>'),
    close: (size) => svg(size, 1.5, '<path d="M18 6 6 18"/><path d="m6 6 12 12"/>'),
    minus: (size) => svg(size, 2, '<path d="M5 12h14"/>'),
    plus: (size) => svg(size, 2, '<path d="M5 12h14"/><path d="M12 5v14"/>'),
  };

  const escapeHtml = (str) =>
    String(str == null ? '' : str).replace(
      /[&<>"']/g,
      (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch])
    );

  /* ── Line item markup — keep in sync with snippets/cart-line.liquid ── */
  const lineHtml = (item) => {
    const title = escapeHtml(item.product_title || item.title);
    const scent = escapeHtml((item.properties && item.properties._scent) || '');
    const img = item.image
      ? `<img src="${item.image}" alt="${title}" width="80" height="80" class="object-cover w-full h-full" loading="lazy">`
      : '';
    return `<li class="overflow-hidden" data-cart-line data-line-key="${escapeHtml(item.key)}">
  <div class="flex gap-4 px-6 py-5">
    <div class="relative w-20 h-20 shrink-0 bg-panel-lift overflow-hidden">${img}</div>
    <div class="flex-1 min-w-0">
      <div class="flex justify-between items-start gap-2">
        <a href="${escapeHtml(item.url)}" class="font-display text-sm text-ink leading-tight hover:text-flame-text transition-colors">${title}</a>
        <button type="button" data-line-remove aria-label="Remove ${title}" class="p-2.5 text-ink-dim hover:text-flame-text transition-colors shrink-0 mt-0.5 -mr-1">${ICONS.close(14)}</button>
      </div>
      <p class="text-xs text-ink-dim mt-1 leading-tight line-clamp-1">${scent}</p>
      <div class="flex items-center justify-between mt-3">
        <div class="flex items-center border border-wire">
          <button type="button" data-line-decrease aria-label="Decrease quantity" class="p-3.5 text-ink-dim hover:text-ink transition-colors">${ICONS.minus(12)}</button>
          <span class="px-3 text-sm text-ink tabular-nums min-w-[2ch] text-center" data-line-qty>${item.quantity}</span>
          <button type="button" data-line-increase aria-label="Increase quantity" class="p-3.5 text-ink-dim hover:text-ink transition-colors">${ICONS.plus(12)}</button>
        </div>
        <p class="text-sm text-ink font-medium tabular-nums" data-line-price>${money(item.final_line_price)}</p>
      </div>
    </div>
  </div>
</li>`;
  };

  const emptyHtml = `<div class="flex flex-col items-center justify-center h-full gap-4 px-6 text-center" data-cart-empty>
  <span class="text-wire">${ICONS.cart(40, 1)}</span>
  <p class="font-display text-xl text-ink">Your cart is empty</p>
  <p class="text-sm text-ink-dim max-w-xs">Add a candle to get started.</p>
  <button type="button" data-cart-close class="mt-2 text-sm text-flame-text hover:text-ink transition-colors underline underline-offset-4">Browse the collection</button>
</div>`;

  /* ── Drawer open/close ── */
  const overlay = document.getElementById('cart-overlay');
  const drawer = document.getElementById('cart-drawer');
  let drawerOpen = false;

  const setDrawer = (open) => {
    if (open === drawerOpen || !drawer || !overlay) return;
    drawerOpen = open;
    drawer.classList.toggle('is-open', open);
    overlay.classList.toggle('is-open', open);
    overlay.setAttribute('aria-hidden', String(!open));
    if (open) lockScroll();
    else unlockScroll();
  };

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-cart-open]')) setDrawer(true);
    else if (e.target.closest('[data-cart-close]')) setDrawer(false);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawerOpen) setDrawer(false);
  });

  /* ── Badge pop ── */
  const popBadges = () => {
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      badge.classList.remove('badge-pop');
      void badge.offsetWidth;
      badge.classList.add('badge-pop');
    });
  };

  /* ── Cart fly dot (button → header cart icon) ── */
  const flyToCart = (fromEl) => {
    if (reducedMotion) return;
    const target = document.querySelector('[data-cart-open]');
    if (!fromEl || !target) return;
    const from = fromEl.getBoundingClientRect();
    const to = target.getBoundingClientRect();
    const dot = document.createElement('div');
    dot.className = 'cart-fly-dot';
    dot.style.left = `${from.left + from.width / 2 - 5}px`;
    dot.style.top = `${from.top + from.height / 2 - 5}px`;
    document.body.appendChild(dot);
    const dx = to.left + to.width / 2 - (from.left + from.width / 2);
    const dy = to.top + to.height / 2 - (from.top + from.height / 2);
    dot.animate(
      [
        { transform: 'translate(0, 0) scale(1)', opacity: 1 },
        { transform: `translate(${dx}px, ${dy}px) scale(0.2)`, opacity: 0.6 },
      ],
      { duration: 500, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
    ).onfinish = () => dot.remove();
  };

  /* ── Render cart state ── */
  const isCartPage = !!document.querySelector('[data-cart-page]');

  const render = (cart) => {
    const count = cart.item_count;

    // Header badges
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      badge.textContent = count > 9 ? '9+' : String(count);
      badge.classList.toggle('hidden', count === 0);
    });

    // Drawer title count
    const titleCount = document.querySelector('[data-cart-title-count]');
    if (titleCount) {
      titleCount.textContent = ` (${count} ${count === 1 ? 'item' : 'items'})`;
      titleCount.classList.toggle('hidden', count === 0);
    }

    // Drawer body (full re-render handles empty ↔ items both ways)
    const body = document.querySelector('[data-cart-body]');
    if (body) {
      body.innerHTML =
        count === 0
          ? emptyHtml
          : `<ul class="divide-y divide-wire-faint" data-cart-lines>${cart.items.map(lineHtml).join('')}</ul>`;
    }

    // Cart page: list exists only when SSR rendered items; reload if it became empty
    if (isCartPage && count === 0) {
      window.location.reload();
      return;
    }
    const pageList = document.querySelector('[data-cart-page] [data-cart-lines]');
    if (pageList) pageList.innerHTML = cart.items.map(lineHtml).join('');

    // Totals
    document.querySelectorAll('[data-cart-subtotal], [data-cart-total]').forEach((el) => {
      el.textContent = money(cart.total_price);
    });

    // Footers hidden when empty
    document.querySelectorAll('[data-cart-footer]').forEach((el) => {
      el.classList.toggle('hidden', count === 0);
    });

    // Free-shipping progress (scaleX — never width)
    if (freeShippingCents > 0) {
      const remaining = freeShippingCents - cart.total_price;
      const ratio = Math.min(cart.total_price / freeShippingCents, 1);
      document.querySelectorAll('[data-ship-progress]').forEach((block) => {
        const msg = block.querySelector('[data-ship-message]');
        if (msg) {
          msg.textContent =
            remaining > 0
              ? `You're ${money(remaining)} away from free shipping`
              : 'Your order ships free.';
        }
        const fill = block.querySelector('.ship-progress-fill');
        if (fill) fill.style.transform = `scaleX(${ratio})`;
      });
    }
  };

  const fetchCart = () => fetch('/cart.js').then((r) => r.json());

  const refresh = () => fetchCart().then(render);

  /* ── Add to cart ── */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-add-to-cart]');
    if (!btn || btn.disabled) return;

    const variantId = parseInt(btn.dataset.variantId, 10);
    if (!variantId) return;

    const form = btn.closest('[data-product-form]');
    const qtyEl = form && form.querySelector('[data-qty-value]');
    const quantity = qtyEl ? parseInt(qtyEl.textContent, 10) || 1 : 1;

    const scent = btn.dataset.scent || '';
    const properties = scent ? { _scent: scent } : {};

    const label = btn.querySelector('[data-add-label]');
    const originalLabel = label ? label.textContent : '';
    btn.disabled = true;

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity, properties }] }),
      });
      if (!res.ok) throw new Error(`Add to cart failed: ${res.status}`);

      flyToCart(btn);
      await refresh();
      popBadges();
      setDrawer(true);

      if (label) {
        label.textContent = 'Added!';
        window.setTimeout(() => {
          label.textContent = originalLabel;
        }, 1800);
      }
    } catch (err) {
      if (label) label.textContent = originalLabel;
    } finally {
      btn.disabled = false;
    }
  });

  /* ── Line quantity changes ── */
  document.addEventListener('click', async (e) => {
    const action = e.target.closest('[data-line-decrease], [data-line-increase], [data-line-remove]');
    if (!action) return;
    const line = action.closest('[data-cart-line]');
    if (!line) return;

    const key = line.dataset.lineKey;
    const qtyEl = line.querySelector('[data-line-qty]');
    const current = qtyEl ? parseInt(qtyEl.textContent, 10) || 0 : 0;
    let next = current;
    if (action.hasAttribute('data-line-increase')) next = current + 1;
    else if (action.hasAttribute('data-line-decrease')) next = current - 1;
    else next = 0; // remove

    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: next }),
      });
      if (!res.ok) throw new Error(`Cart change failed: ${res.status}`);
      const cart = await res.json();
      render(cart);
    } catch (err) {
      refresh();
    }
  });

  /* ── Initial paint: ship progress + totals from live cart ── */
  refresh();
})();
